import { useState, useEffect } from 'react';
import { messaging, getToken, onMessage } from '../utils/firebase';
import api from '../utils/api';
import { toast } from 'sonner';

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

export const usePushNotifications = () => {
  const [permission, setPermission] = useState(Notification.permission);
  const [token, setToken] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (!messaging) {
      console.warn('🔕 Firebase Messaging not supported in this browser');
      return;
    }

    if (isInitialized) {
      return;
    }

    // Request permission and get token
    const initializeNotifications = async () => {
      try {
        console.log('🔔 Inicializando notificaciones push...');
        console.log('   Permission actual:', Notification.permission);
        
        // Check if permission is already granted
        if (Notification.permission === 'granted') {
          console.log('✅ Permisos ya concedidos, obteniendo token...');
          await requestToken();
        } else {
          console.log('⚠️  Permisos no concedidos aún. Esperando clic en botón 🔔');
        }

        // Listen for foreground messages
        const unsubscribe = onMessage(messaging, (payload) => {
          console.log('📩 Foreground message received:', payload);
          
          toast.success(payload.notification?.title || 'Nueva Notificación', {
            description: payload.notification?.body || '',
            duration: 5000
          });
        });

        setIsInitialized(true);
        return unsubscribe;
      } catch (error) {
        console.error('❌ Error initializing notifications:', error);
      }
    };

    initializeNotifications();
  }, [isInitialized]);

  const requestToken = async () => {
    try {
      console.log('🎫 Intentando obtener FCM token...');
      
      // Get the existing unified service worker registration
      const registration = await navigator.serviceWorker.getRegistration();
      
      if (!registration) {
        console.error('❌ No service worker registered');
        return;
      }

      console.log('✅ Service Worker encontrado:', registration.scope);

      const currentToken = await getToken(messaging, { 
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: registration // Use unified SW
      });
      
      if (currentToken) {
        console.log('✅ FCM Token obtained:', currentToken.substring(0, 30) + '...');
        setToken(currentToken);
        
        // Send token to backend
        console.log('📤 Enviando token al backend...');
        await api.post('/api/fcm/token', { token: currentToken });
        console.log('✅ Token guardado en backend correctamente');
        
        toast.success('🔔 Notificaciones configuradas', {
          description: 'Recibirás alertas de nuevas ventas',
          duration: 3000
        });
      } else {
        console.warn('⚠️  No FCM token available. User may have blocked notifications.');
      }
    } catch (error) {
      console.error('❌ Error getting FCM token:', error);
      console.error('   Error details:', error.message);
    }
  };

  const requestPermission = async () => {
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      
      if (perm === 'granted') {
        await requestToken();
        toast.success('¡Notificaciones activadas!', {
          description: 'Recibirás alertas de nuevas ventas'
        });
      } else {
        toast.error('Permiso denegado', {
          description: 'No podrás recibir notificaciones'
        });
      }
    } catch (error) {
      console.error('Error requesting permission:', error);
      toast.error('Error al solicitar permisos');
    }
  };

  return {
    permission,
    token,
    requestPermission,
    isSupported: !!messaging
  };
};
